import { Icon } from '@iconify/react';
import { useMutation, useQuery } from '@tanstack/react-query';
/**
 * Her morning, his way.
 *
 * The old screen was six stacked buckets — Late, Right now, Today, Upcoming,
 * Waiting on me, Done today — each a heading over a list of identical cards.
 * Six lists is not a priority; a housekeeper opening her phone at 08:00 had
 * to read all of them to find out what she is doing at 08:00.
 *
 * His screen answers that in the first card and nothing above it: the job she
 * is on now, big, with the one button that moves it. Everything else is
 * demoted — what comes after it, how the day is going, and the two things she
 * does that are not a task.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, type Operation, api } from '../api';
import { Avatar, useAvatarUrl } from '../components/Avatar';
import { EnableNotifications } from '../components/EnableNotifications';
import { mapHref } from '../components/LocationLinks';
import { toastError, toastOk } from '../components/Toasts';
import { Sheet } from '../components/ui/Sheet';
import { SkeletonScreen } from '../components/ui/Skeleton';
import { getLang, pickTranslation, t } from '../i18n';
import { get, invalidate } from '../query';
import { ClockCard } from './ClockCard';
import { StaffTour } from './StaffTour';

// Subset of /v1/me used to decide whether the first-time tour should show.
// Combined shape covering both features that read /v1/me here:
// - the header avatar + greeting (avatars agent)
// - the first-visit tour gate (staff tour agent)
// We fetch /v1/me once and let both features derive from this single state.
interface MeRow {
  id: string;
  role: 'owner' | 'staff';
  full_name: string;
  avatar_r2_key: string | null;
  staff_tour_seen_at: string | null;
}

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return t('Good morning');
  if (h < 18) return t('Good afternoon');
  return t('Good evening');
}

type Bucket = 'late' | 'now' | 'today' | 'upcoming' | 'waiting_me' | 'done_today';

export function StaffToday() {
  const [proofOp, setProofOp] = useState<Operation | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [tourSeen, setTourSeen] = useState(false);
  const location = useLocation();
  const nav = useNavigate();
  // Banner shown once after StaffReceipt navigates back here. We swallow
  // the location.state on first render so a refresh doesn't re-trigger it.
  const [receiptSent, setReceiptSent] = useState(
    Boolean((location.state as { receiptProposalSent?: boolean } | null)?.receiptProposalSent),
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only on purpose. This effect exists to swallow the location.state that receiptSent was seeded from; listing receiptSent, nav or location.pathname would re-run it after the replace() it performs, and re-navigating on every state change would fight the router mid-render.
  useEffect(() => {
    if (receiptSent) {
      // Clear the nav state so a refresh doesn't re-show the banner.
      nav(location.pathname, { replace: true, state: null });
    }
  }, []);

  // Her day, kept warm. The thirty-second poll is the same one this screen
  // already ran; what is new is that the answer survives leaving the screen,
  // so coming back from a task does not blank the page and start again.
  //
  // No `tab` param means the server's "everything" view: every task that
  // isn't yet approved/cancelled comes back in full regardless of how old its
  // updated_at is, so a task scheduled for next week can never be silently
  // pushed out by a busy day of approvals elsewhere in the house. Only the
  // approved/cancelled history is paginated (next_cursor/has_more) — this
  // screen has never needed to page through history, so it isn't wired up
  // here; a screen that wants older completed tasks would pass `cursor`.
  const opsQ = useQuery({
    queryKey: ['/v1/operations'],
    queryFn: get<{ items: Operation[]; next_cursor: string | null; has_more: boolean }>,
    refetchInterval: 30_000,
  });
  const ops = opsQ.data?.items ?? [];

  // One /v1/me covers the header greeting, the avatar, and the first-visit
  // tour gate. Shared with every other screen that asks the same question.
  const meQ = useQuery({ queryKey: ['/v1/me'], queryFn: get<{ user: MeRow }> });
  const me = meQ.data?.user ?? null;
  // Same cached signed-URL lookup the header Avatar and every other avatar
  // in the app already use (GET /v1/uploads/avatar-url/:user_id under the
  // hood). Resolves to `undefined` while loading and `null` once we know
  // there is no photo — the welcome slide falls back to initials either way.
  const meAvatarUrl = useAvatarUrl(me?.id);

  const showTour = me?.role === 'staff' && me.staff_tour_seen_at === null && !tourSeen;

  const transition = useMutation({
    mutationFn: ({ opId, to }: { opId: string; to: string }) =>
      api(`/v1/operations/${opId}/transitions/${to}`, { method: 'POST', body: '{}' }),
    onSuccess: (_r, { to }) => {
      if (to === 'waiting_approval') toastOk(t('Sent to your owner'));
    },
    onError: (e) => toastError(e),
    onSettled: () => invalidate('/v1/operations'),
  });

  const buckets: Record<Bucket, Operation[]> = {
    late: [],
    now: [],
    today: [],
    upcoming: [],
    waiting_me: [],
    done_today: [],
  };
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  for (const op of ops) {
    // Late work goes first and stays first. The server decides this — until
    // now nothing on this screen distinguished a task whose deadline passed
    // yesterday from one due next week, so they sat in the same list and the
    // late one lost.
    if (op.state === 'overdue' || op.state === 'escalated') {
      buckets.late.push(op);
    } else if (op.state === 'in_progress' || op.state === 'waiting_proof') {
      buckets.now.push(op);
    } else if (op.state === 'sent' || op.state === 'seen' || op.state === 'scheduled') {
      // Pending work splits between TODAY (scheduled-for-today or no
      // specific date) and UPCOMING (scheduled for a future day). The
      // upcoming bucket is grouped by date so the staff sees "tomorrow",
      // "Sat 23/5", etc. instead of one long list.
      const when = op.scheduledFor ?? op.dueAt ?? null;
      if (!when) {
        buckets.today.push(op);
      } else {
        const whenMs = new Date(when).getTime();
        if (whenMs <= endOfDay.getTime()) buckets.today.push(op);
        else buckets.upcoming.push(op);
      }
    } else if (op.state === 'rejected') {
      buckets.waiting_me.push(op);
    } else if (
      (op.state === 'approved' || op.state === 'waiting_approval') &&
      new Date(op.updatedAt ?? op.createdAt).getTime() >= startOfDay.getTime()
    ) {
      buckets.done_today.push(op);
    }
  }
  // Chronological inside every bucket. "Coming next" that is not in the order
  // it is coming in is a list, and she has to read all of it — which is the
  // thing this screen exists to stop.
  const byTime = (a: Operation, b: Operation) =>
    new Date(a.scheduledFor ?? a.dueAt ?? a.createdAt).getTime() -
    new Date(b.scheduledFor ?? b.dueAt ?? b.createdAt).getTime();
  buckets.late.sort(byTime);
  buckets.now.sort(byTime);
  buckets.today.sort(byTime);
  buckets.upcoming.sort(byTime);

  // The one job she is on. Late beats running beats waiting — the same order
  // the six buckets were in, collapsed into a single answer.
  const queue = [...buckets.late, ...buckets.now, ...buckets.today];
  const hero = queue[0] ?? null;
  const next = queue.slice(1);

  const doneCount = buckets.done_today.length;
  const totalToday = queue.length + doneCount;
  const pct = totalToday > 0 ? Math.round((doneCount / totalToday) * 100) : 0;

  // His announcement band. There is no broadcast in this product — but a task
  // that belongs to an evening carries that evening on the row, and "guests at
  // 19:30" is exactly what it is. Real, denormalised, already on the wire.
  const eventOp = queue.find((o) => o.event_title) ?? null;

  const today = new Date().toLocaleDateString(localeFor(getLang()), {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });

  if (opsQ.isPending) return <SkeletonScreen rows={3} />;

  // A failed fetch is not "you have no work today" — it is "we don't know".
  // Falling through to the empty-state buckets below used to render the
  // exact same screen for both, which is the one failure mode this product
  // cannot afford: a housekeeper who never learns her tasks didn't load has
  // no reason to think anything is wrong. Same pattern as OperationDetail /
  // Profile — a plain card with the server's own message and a retry.
  if (opsQ.isError) {
    return (
      <div className="mx-auto w-full max-w-lg pt-10">
        <div className="rounded-[2.5rem] border border-urgent/25 bg-urgent/10 p-8 text-center">
          <Icon icon="ph:wifi-slash-bold" className="mx-auto mb-3 text-3xl text-urgent" />
          <p className="text-sm font-bold text-urgent">
            {opsQ.error instanceof ApiError ? opsQ.error.message : t("Couldn't load your tasks.")}
          </p>
          <button
            type="button"
            onClick={() => void opsQ.refetch()}
            className="mt-4 rounded-2xl bg-urgent/10 px-6 py-3 text-xs font-bold uppercase tracking-widest text-urgent transition-colors active:bg-urgent/20"
          >
            {t('Tap to retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-40">
      {/* His header. The layout's own bar is desktop-only now — on a phone
          his screens carry their heading, and two headings is one too many. */}
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight">
            {me ? `${timeOfDayGreeting()}, ${me.full_name.split(' ')[0]}` : timeOfDayGreeting()}
          </h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {today}
          </p>
        </div>
        {me && (
          <Link to="/profile" className="relative shrink-0" aria-label={t('Profile')}>
            <Avatar name={me.full_name} userId={me.id} size="md" shape="square" />
            <span className="absolute -bottom-1 -end-1 h-4 w-4 rounded-full border-2 border-background bg-wealth" />
          </Link>
        )}
      </header>

      {/* One tap when she arrives, one when she leaves. */}
      <ClockCard />

      <EnableNotifications />

      {receiptSent && (
        <div className="rounded-[2rem] border border-wealth/25 bg-wealth/10 p-5">
          <div className="text-sm font-bold text-wealth">{t('Sent to your owner')}</div>
          <div className="mt-1 text-xs font-medium text-muted-foreground">
            {t('They will see the proposal and approve or reject it.')}
          </div>
          <button
            type="button"
            className="mt-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground underline"
            onClick={() => setReceiptSent(false)}
          >
            {t('Dismiss')}
          </button>
        </div>
      )}

      {eventOp?.event_title && (
        <section>
          <div className="flex items-center gap-4 rounded-[2.5rem] border border-task/20 bg-task/10 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-task/15 text-task">
              <Icon icon="ph:megaphone-fill" className="text-xl" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-snug text-task" dir="auto">
                {eventOp.event_title}
              </p>
              {eventOp.event_starts_at && (
                <p className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-task/70">
                  {new Date(eventOp.event_starts_at).toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="space-y-4">
        <h2 className="px-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          {t('Right now')}
        </h2>
        {hero ? (
          <HeroCard
            op={hero}
            onStart={() => transition.mutate({ opId: hero.id, to: 'in_progress' })}
            onProof={() => setProofOp(hero)}
            onDone={() => transition.mutate({ opId: hero.id, to: 'waiting_approval' })}
          />
        ) : (
          <div className="rounded-[2.5rem] border border-border bg-card p-8 text-center">
            <p className="text-sm font-bold text-muted-foreground">
              {t('Nothing to do right now.')}
            </p>
          </div>
        )}
      </section>

      {next.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t('Coming next')}
            </h2>
            <span className="text-[10px] font-bold text-muted-foreground">
              {t('{{n}} tasks today').replace('{{n}}', String(totalToday))}
            </span>
          </div>
          <div className="space-y-3">
            {next.map((op) => (
              <OpRow key={op.id} op={op} />
            ))}
          </div>
        </section>
      )}

      {buckets.waiting_me.length > 0 && (
        <section className="space-y-4">
          <h2 className="px-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            {t('Waiting on me')}
          </h2>
          <div className="space-y-3">
            {buckets.waiting_me.map((op) => (
              <OpRow key={op.id} op={op} tone="urgent" />
            ))}
          </div>
        </section>
      )}

      {buckets.upcoming.length > 0 && <UpcomingByDay ops={buckets.upcoming} />}

      {totalToday > 0 && (
        <section className="space-y-4">
          <h2 className="px-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            {t("Today's progress")}
          </h2>
          <div className="rounded-[2.5rem] border border-border bg-card p-6 shadow-sm">
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <p className="font-heading text-2xl font-bold tracking-tight">
                  {t('{{done}} of {{total}} tasks')
                    .replace('{{done}}', String(doneCount))
                    .replace('{{total}}', String(totalToday))}
                </p>
                <p className="text-xs text-muted-foreground">{t('Completed today')}</p>
              </div>
              {buckets.late.length > 0 && (
                <div className="text-end">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-magic">
                    {t('Late')}
                  </p>
                  <p className="text-sm font-bold tabular-nums">{buckets.late.length}</p>
                </div>
              )}
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-wealth" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </section>
      )}

      <section className="grid grid-cols-2 gap-3">
        <Link
          to="/today/receipt"
          className="flex flex-col items-center gap-2 rounded-[2rem] bg-secondary p-5 transition-colors active:bg-border"
        >
          <Icon icon="ph:receipt-bold" className="text-2xl" />
          <span className="text-[10px] font-bold uppercase tracking-widest">
            {t('Scan a receipt')}
          </span>
        </Link>
        <button
          type="button"
          onClick={() => setIssueOpen(true)}
          disabled={queue.length === 0}
          className="flex flex-col items-center gap-2 rounded-[2rem] bg-secondary p-5 transition-colors active:bg-border disabled:opacity-40"
        >
          <Icon icon="ph:warning-circle-bold" className="text-2xl text-urgent" />
          <span className="text-[10px] font-bold uppercase tracking-widest">
            {t('Report issue')}
          </span>
        </button>
      </section>

      {/* The camera, under the thumb. Proof is the thing she is asked for
          most, and it used to live two taps inside a card. */}
      {hero && (
        <button
          type="button"
          onClick={() => setProofOp(hero)}
          aria-label={t('Send proof')}
          className="fixed bottom-28 end-6 z-40 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-2xl shadow-primary/40 transition-transform active:scale-90 sm:bottom-10"
        >
          <Icon icon="ph:camera-fill" className="text-3xl" />
        </button>
      )}

      {proofOp && (
        <ProofSheet
          op={proofOp}
          onClose={() => setProofOp(null)}
          onDone={() => {
            setProofOp(null);
            invalidate('/v1/operations');
          }}
        />
      )}

      {issueOpen && (
        <IssueSheet
          ops={queue}
          onClose={() => setIssueOpen(false)}
          onSent={() => {
            setIssueOpen(false);
            toastOk(t('Sent to your owner'));
            invalidate('/v1/operations');
          }}
        />
      )}

      {showTour && me && (
        <StaffTour
          fullName={me.full_name}
          // undefined (still resolving) collapses to initials same as null
          // (no photo set) — SlideWelcome only special-cases a truthy URL.
          avatarUrl={meAvatarUrl ?? null}
          onClose={() => {
            // Local flip so the overlay disappears immediately. /v1/me will
            // also reflect staff_tour_seen_at on the next mount, so even if
            // this state were lost the server would prevent re-show.
            setTourSeen(true);
            invalidate('/v1/me');
          }}
        />
      )}
    </div>
  );
}

function localeFor(lang: string): string {
  return lang === 'he' ? 'he-IL' : lang === 'ur' ? 'ur-PK' : lang === 'tl' ? 'fil-PH' : 'en-US';
}

/**
 * The job she is on, at the size of the decision.
 *
 * One card, one button. Which button depends on where the task is: work that
 * has not begun starts, work in flight either sends its photograph or is
 * handed over for approval.
 */
function HeroCard({
  op,
  onStart,
  onProof,
  onDone,
}: {
  op: Operation;
  onStart: () => void;
  onProof: () => void;
  onDone: () => void;
}) {
  const running = op.state === 'in_progress' || op.state === 'waiting_proof';
  const late = op.state === 'overdue' || op.state === 'escalated';
  const when = op.dueAt ?? op.scheduledFor ?? null;

  const action = !running
    ? { label: t('Start work'), run: onStart }
    : op.proofRequired
      ? { label: t('Send proof'), run: onProof }
      : { label: t('Mark done'), run: onDone };

  return (
    <div className="relative overflow-hidden rounded-[2.5rem] border border-border bg-card p-6 shadow-sm">
      <div className={`absolute end-0 top-0 h-full w-1.5 ${late ? 'bg-magic' : 'bg-primary'}`} />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            {when && (
              <span
                className={`rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-tighter ${
                  late ? 'bg-magic/10 text-magic' : 'bg-primary/10 text-primary'
                }`}
              >
                {`${late ? t('Was due') : t('Due')} ${new Date(when).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`}
              </span>
            )}
            {op.priority !== 'normal' && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[8px] font-bold uppercase tracking-tighter text-muted-foreground">
                {t(op.priority === 'emergency' ? 'Emergency' : 'Important')}
              </span>
            )}
          </div>
          <h3 className="font-heading text-2xl font-bold tracking-tight" dir="auto">
            {pickTranslation(op.titleTranslations, op.title)}
          </h3>
          {op.locationText && (
            <a
              href={mapHref(op.locationText)}
              target="_blank"
              rel="noreferrer"
              className="mt-1 block truncate text-xs text-muted-foreground underline-offset-2 hover:underline"
              dir="auto"
            >
              {op.locationText}
            </a>
          )}
        </div>
        <Link
          to={`/op/${op.id}`}
          aria-label={t('Open')}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-secondary text-muted-foreground transition-transform active:scale-95"
        >
          <Icon icon="ph:caret-right-bold" className="text-xl rtl:rotate-180" />
        </Link>
      </div>

      <div className="space-y-4">
        {op.proofRequired && (
          <div className="flex items-center gap-3 text-sm font-medium">
            <Icon icon="ph:camera-bold" className="text-lg text-primary" />
            <span>{t('Photo proof required')}</span>
          </div>
        )}
        {op.checklist && op.checklist.length > 0 && (
          <div className="flex items-center gap-3 text-sm font-medium">
            <Icon icon="ph:list-checks-bold" className="text-lg text-primary" />
            <span>
              {t('{{done}} of {{total}} steps done')
                .replace('{{done}}', String(op.checklist.filter((c) => c.done).length))
                .replace('{{total}}', String(op.checklist.length))}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={action.run}
          className="w-full rounded-2xl bg-primary py-5 text-base font-bold text-primary-foreground shadow-xl shadow-primary/20 transition-transform active:scale-[0.98]"
        >
          {action.label}
        </button>
      </div>
    </div>
  );
}

/** Everything after the one she is on: a row, an hour, and a way in. */
function OpRow({ op, tone }: { op: Operation; tone?: 'urgent' }) {
  const when = op.scheduledFor ?? op.dueAt ?? null;
  return (
    <Link
      to={`/op/${op.id}`}
      className={`flex w-full items-center justify-between gap-3 rounded-[2rem] border border-border bg-card p-5 transition-transform active:scale-[0.99] ${
        tone === 'urgent' ? 'border-s-4 border-s-urgent' : ''
      }`}
    >
      <div className="flex min-w-0 items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
          <Icon
            icon={op.proofRequired ? 'ph:camera-bold' : 'ph:checks-bold'}
            className="text-2xl"
          />
        </div>
        <div className="min-w-0 text-start">
          <h4 className="truncate text-sm font-bold" dir="auto">
            {pickTranslation(op.titleTranslations, op.title)}
          </h4>
          <p className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {when
              ? t('Start at {{time}}').replace(
                  '{{time}}',
                  new Date(when).toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                )
              : t('No time set')}
          </p>
        </div>
      </div>
      <Icon icon="ph:caret-right-bold" className="shrink-0 text-muted-foreground rtl:rotate-180" />
    </Link>
  );
}

/**
 * Something is wrong and it is about a job.
 *
 * Not a new feature — an update of kind `issue` is a thing this product has
 * always accepted, and the only way to write one was to open the task and
 * find the right chip. His screen puts it beside the receipt scanner, where
 * a person who has just found a broken thing is looking.
 */
function IssueSheet({
  ops,
  onClose,
  onSent,
}: {
  ops: Operation[];
  onClose: () => void;
  onSent: () => void;
}) {
  const [opId, setOpId] = useState(ops[0]?.id ?? '');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    if (!opId || !body.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/operations/${opId}/updates`, {
        method: 'POST',
        body: JSON.stringify({ kind: 'issue', body: body.trim(), source_lang: getLang() }),
      });
      onSent();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={t('Report issue')} onClose={onClose}>
      <select className="input" value={opId} onChange={(e) => setOpId(e.target.value)}>
        {ops.map((o) => (
          <option key={o.id} value={o.id}>
            {pickTranslation(o.titleTranslations, o.title)}
          </option>
        ))}
      </select>
      <textarea
        className="input min-h-[100px]"
        placeholder={t('What is wrong?')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        dir="auto"
      />
      {err && <div className="text-sm text-urgent">{err}</div>}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={busy || !body.trim() || !opId}
          onClick={send}
        >
          {busy ? '…' : t('Send')}
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          {t('Cancel')}
        </button>
      </div>
    </Sheet>
  );
}

function UpcomingByDay({ ops }: { ops: Operation[] }) {
  // Group by the local-day string the staff will read on their phone.
  // "Tomorrow", "Sat 23/5", "Mon 25/5" — the relative form is friendlier
  // than raw ISO dates for the next ~7 days.
  const lang = getLang();
  const locale =
    lang === 'he' ? 'he-IL' : lang === 'ur' ? 'ur-PK' : lang === 'tl' ? 'fil-PH' : 'en-US';
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  const dayAfter = new Date(tomorrow);
  dayAfter.setDate(dayAfter.getDate() + 1);

  function dayLabel(d: Date): string {
    if (d.getTime() >= tomorrow.getTime() && d.getTime() < dayAfter.getTime()) {
      return t('Tomorrow');
    }
    return d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit' });
  }

  const groups = new Map<string, { label: string; ops: Operation[] }>();
  for (const op of ops) {
    const when = new Date(op.scheduledFor ?? op.dueAt ?? op.createdAt);
    when.setHours(0, 0, 0, 0);
    const key = when.toISOString().slice(0, 10);
    if (!groups.has(key)) groups.set(key, { label: dayLabel(when), ops: [] });
    groups.get(key)!.ops.push(op);
  }

  return (
    <section>
      <h2 className="section-title">{t('Upcoming')}</h2>
      <div className="space-y-4">
        {Array.from(groups.entries()).map(([key, group]) => (
          <div key={key} className="space-y-2">
            <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
              {group.label}
            </div>
            {group.ops.map((op) => (
              <OpRow key={op.id} op={op} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/** The one place proof gets uploaded. The task page shows the same sheet —
 *  only the assignee may send proof, so there is one flow, not two. */
export function ProofSheet({
  op,
  onClose,
  onDone,
}: {
  op: Operation;
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [voice, setVoice] = useState<Blob | null>(null);
  const [recording, setRecording] = useState<MediaRecorder | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function record() {
    if (recording) {
      recording.stop();
      setRecording(null);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        setVoice(new Blob(chunks, { type: 'audio/webm' }));
        for (const tk of stream.getTracks()) tk.stop();
      };
      rec.start();
      setRecording(rec);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function uploadToR2(
    blob: Blob | File,
    kind: 'proof' | 'voice',
    contentType: string,
  ): Promise<string> {
    const sign = await api<{
      put_url: string;
      r2_key: string;
      required_headers: Record<string, string>;
    }>('/v1/uploads/sign', {
      method: 'POST',
      body: JSON.stringify({
        kind,
        content_type: contentType,
        byte_length: blob.size,
      }),
    });
    const put = await fetch(sign.put_url, {
      method: 'PUT',
      headers: sign.required_headers,
      body: blob,
    });
    if (!put.ok) throw new Error(`R2 upload ${put.status}`);
    return sign.r2_key;
  }

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      let r2_key: string | undefined;
      let proofType: 'photo' | 'voice' | 'text' = 'text';
      if (file) {
        r2_key = await uploadToR2(file, 'proof', file.type || 'image/jpeg');
        proofType = 'photo';
      } else if (voice) {
        r2_key = await uploadToR2(voice, 'voice', 'audio/webm');
        proofType = 'voice';
      }
      await api(`/v1/operations/${op.id}/proofs`, {
        method: 'POST',
        body: JSON.stringify({
          type: proofType,
          r2_key,
          text_body: text || undefined,
        }),
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={t('Send proof')} onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        {pickTranslation(op.titleTranslations, op.title)}
      </p>

      <label className="block">
        <span className="text-sm text-muted-foreground">{t('Photo (camera)')}</span>
        <input
          className="mt-1 block w-full text-sm"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </label>

      <div>
        <button
          type="button"
          onClick={record}
          className={`w-full text-sm rounded-xl px-4 py-3 ${
            recording
              ? 'bg-danger/10 text-danger'
              : voice
                ? 'bg-success/10 text-success'
                : 'bg-secondary text-foreground/80'
          }`}
        >
          {recording
            ? t('Stop recording')
            : voice
              ? t('Voice ready · tap to re-record')
              : t('Voice note')}
        </button>
      </div>

      <textarea
        className="input min-h-[80px]"
        placeholder={t('Short note (optional)')}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      {err && <div className="text-danger text-sm">{err}</div>}

      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={busy || (!file && !text && !voice)}
          onClick={submit}
        >
          {busy ? '…' : t('Send')}
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          {t('Cancel')}
        </button>
      </div>
    </Sheet>
  );
}
